import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Home, Shield, Lock, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TwoFactorAuth = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [setupMode, setSetupMode] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [password, setPassword] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [disabling, setDisabling] = useState(false);

  useEffect(() => {
    fetchUser();
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      toast.error('Please log in first');
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const handleSetupStart = async () => {
    if (!password) {
      toast.error('Please enter your password');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/auth/2fa/setup`,
        { password },
        { withCredentials: true }
      );
      setQrCode(response.data.qr_code);
      setSecret(response.data.manual_entry);
      setSetupMode(true);
      setPassword('');
      toast.success('Scan the QR code with your authenticator app');
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Incorrect password');
      } else {
        toast.error('Failed to setup 2FA');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!verifyCode || verifyCode.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/auth/2fa/verify`,
        { code: verifyCode },
        { withCredentials: true }
      );
      toast.success('Two-Factor Authentication enabled!');
      setSetupMode(false);
      setQrCode('');
      setSecret('');
      setVerifyCode('');
      fetchUser();
    } catch (error) {
      toast.error('Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    if (!password) {
      toast.error('Please enter your password');
      return;
    }

    if (!window.confirm('Are you sure you want to disable Two-Factor Authentication?')) {
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/auth/2fa/disable`,
        { password },
        { withCredentials: true }
      );
      toast.success('Two-Factor Authentication disabled');
      setDisabling(false);
      setPassword('');
      fetchUser();
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Incorrect password');
      } else {
        toast.error('Failed to disable 2FA');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading && !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 to-orange-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-orange-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100 p-4">
      {/* Navigation */}
      <nav className="bg-white border-b border-orange-100 rounded-t-xl mb-8">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center text-gray-600 hover:text-orange-600 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Dashboard
          </button>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto">
        <Card className="p-8 shadow-2xl">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Shield className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Playfair Display' }}>
              Two-Factor Authentication
            </h1>
            <p className="text-gray-600">Add an extra layer of security to your account</p>
          </div>

          {user?.totp_enabled ? (
            // 2FA is enabled - show disable option
            <div className="space-y-6">
              <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6 text-center">
                <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-green-900 mb-2">2FA is Active</h3>
                <p className="text-green-700">Your account is protected with Two-Factor Authentication</p>
              </div>

              {!disabling ? (
                <Button
                  onClick={() => setDisabling(true)}
                  variant="outline"
                  className="w-full border-red-300 text-red-600 hover:bg-red-50"
                >
                  Disable Two-Factor Authentication
                </Button>
              ) : (
                <div className="space-y-4">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <AlertCircle className="w-5 h-5 text-amber-600 inline mr-2" />
                    <span className="text-amber-800 font-medium">Enter your password to disable 2FA</span>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter your password"
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div className="flex space-x-3">
                    <Button
                      onClick={() => {
                        setDisabling(false);
                        setPassword('');
                      }}
                      variant="outline"
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleDisable}
                      disabled={loading}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                    >
                      {loading ? 'Disabling...' : 'Confirm Disable'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : !setupMode ? (
            // 2FA is disabled - show setup option
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h3 className="font-bold text-blue-900 mb-3 text-lg">Why Enable 2FA?</h3>
                <ul className="space-y-2 text-blue-800">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Protect your property listings from unauthorized access</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Secure buyer inquiries and contact information</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Prevent account takeover and fraud</span>
                  </li>
                </ul>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Enter Your Password to Continue
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Your current password"
                    className="pl-10"
                    onKeyPress={(e) => e.key === 'Enter' && handleSetupStart()}
                  />
                </div>
              </div>

              <Button
                onClick={handleSetupStart}
                disabled={loading}
                className="w-full bg-green-600 hover:bg-green-700 text-white"
              >
                {loading ? 'Setting up...' : 'Enable Two-Factor Authentication'}
              </Button>
            </div>
          ) : (
            // Setup mode - show QR code and verification
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h3 className="font-bold text-blue-900 mb-3 text-lg">Setup Instructions</h3>
                <ol className="space-y-2 text-blue-800 list-decimal list-inside">
                  <li>Download an authenticator app (Google Authenticator, Authy, etc.)</li>
                  <li>Scan the QR code below with your app</li>
                  <li>Enter the 6-digit code from your app to verify</li>
                </ol>
              </div>

              <div className="bg-white border-2 border-gray-300 rounded-lg p-6 text-center">
                {qrCode && (
                  <img src={qrCode} alt="2FA QR Code" className="mx-auto mb-4" />
                )}
                <p className="text-sm text-gray-600 mb-2">Or enter this code manually:</p>
                <code className="bg-gray-100 px-4 py-2 rounded text-sm font-mono">{secret}</code>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Enter 6-Digit Code from Authenticator App
                </label>
                <Input
                  type="text"
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="text-center text-2xl tracking-widest"
                  maxLength={6}
                />
              </div>

              <div className="flex space-x-3">
                <Button
                  onClick={() => {
                    setSetupMode(false);
                    setQrCode('');
                    setSecret('');
                    setVerifyCode('');
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleVerify}
                  disabled={loading || verifyCode.length !== 6}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                >
                  {loading ? 'Verifying...' : 'Verify & Enable'}
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default TwoFactorAuth;
